import moment from "moment-timezone";

export interface TimezoneOption {
	original: string;
	offset: number;
	formatted: string;
}

// Bare GMT aliases are noise in a picker — they duplicate the offset already
// shown in every formatted label and are not real locations.
const EXCLUDED_TIMEZONES = ["GMT", "GMT+0", "GMT-0", "GMT0"];

/**
 * Additional ids excluded from SCHEDULING pickers only (LSP-17619).
 *
 * EST / HST / MST / ROC are absent from java.time's tzdb, which is what kotlinx-datetime's
 * TimeZone.availableZoneIds delegates to on Android — so a scheduling zone picked from this list is
 * stored and then silently rejected on contact-mobile, dropping that user to their device zone. The
 * first three are also fixed-offset legacy aliases that never observe DST ("EST" is UTC-5 all year),
 * which is the drift the sticky-timezone work exists to remove.
 *
 * Deliberately NOT folded into EXCLUDED_TIMEZONES above: that list also backs TimezonePicker.vue,
 * which edits an ORGANISATION's timezone in settings and the welcome wizard. Excluding these there
 * would render a blank selection for any org already storing one — a regression in a field this
 * change is not about.
 */
export const SCHEDULING_EXCLUDED_TIMEZONES = [...EXCLUDED_TIMEZONES, "EST", "HST", "MST", "ROC"];

/** True when a zone id may appear in a general timezone picker. */
function isSelectableTimezone(zoneId: string): boolean {
	return !zoneId.startsWith("Etc/") && !EXCLUDED_TIMEZONES.includes(zoneId);
}

/**
 * Prefixes no scheduling picker may offer, screened rather than enumerated.
 *
 * `Etc/*` are fixed-offset aliases with no DST. `SystemV/*` are legacy 4.3BSD names that java.time still
 * ships (13 of them) and moment-timezone does not know at all — so contact-mobile can offer one while the
 * API's `moment.tz.zone()` check rejects it.
 *
 * Kept in step with contact-mobile's NON_SELECTABLE_SCHEDULING_ZONE_PREFIXES. The cross-platform parity
 * guards compare the excluded ID LISTS, so a prefix added on one side only is invisible to them — which
 * is exactly how these two drifted: `SystemV/` was added to mobile's predicate and not to this one.
 */
const NON_SELECTABLE_SCHEDULING_PREFIXES = ["Etc/", "SystemV/"];

/**
 * True when a zone id may be OFFERED FOR, or REMEMBERED AS, a scheduling timezone.
 *
 * The single source of that rule on web: getSchedulingTimezones() builds the Schedule Call picker
 * from it and resolveSchedulingTimezone() (helpers/schedulingTimezone.ts) refuses to seed the picker
 * with anything it rejects, so a stored value can never select nothing. contact-api and
 * contact-mobile cannot import this and keep their own copies, pinned here by parity guard tests.
 */
export function isSelectableSchedulingTimezone(zoneId: string): boolean {
	return (
		!NON_SELECTABLE_SCHEDULING_PREFIXES.some((prefix) => zoneId.startsWith(prefix)) && !SCHEDULING_EXCLUDED_TIMEZONES.includes(zoneId)
	);
}

function buildTimezoneOptions(predicate: (zoneId: string) => boolean): TimezoneOption[] {
	const formatted: TimezoneOption[] = [];

	for (const zone of moment.tz.names()) {
		if (!predicate(zone)) {
			continue;
		}

		formatted.push({
			original: zone,
			offset: moment.tz(zone).utcOffset(),
			formatted: `(GMT${moment.tz(zone).format("Z")}) ${zone}`
		});
	}

	return formatted;
}

/** General-purpose picker list (organisation timezone, welcome wizard). */
export function getTimezones(): TimezoneOption[] {
	return buildTimezoneOptions(isSelectableTimezone);
}

/** Schedule Call picker list — additionally omits the ids contact-mobile cannot parse. */
export function getSchedulingTimezones(): TimezoneOption[] {
	return buildTimezoneOptions(isSelectableSchedulingTimezone);
}
